import { ApiResponse } from '@/domain'
import { CreateUserData, UpdateUserData } from '@/domain/auth/auth'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { userApi } from '../../rest/modules/auth-api'
import { useToast } from '../use-toast'

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...userKeys.lists(), { filters }] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
}

export const useUsers = (params?: { page?: number; limit?: number; search?: string; roles?: string[]; status?: string }) => {
  const apiParams = params ? {
    ...params,
    roles: params.roles?.join(','),
  } : undefined

  return useQuery({
    queryKey: userKeys.list(params || {}),
    queryFn: () => userApi.getUsers(apiParams),
    staleTime: 0,
    select: (response) => {
      if (!response) return { data: [], total: 0, page: 1, limit: 10, totalPages: 0 }
      return response
    },
  })
}

export const useUser = (id: string) => {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => userApi.getUser(id),
    enabled: !!id,
  })
}

export const useCreateUser = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (userData: CreateUserData) => userApi.createUser(userData),
    onSuccess: (newUser) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      toast({
        title: "User Created",
        description: `${newUser.fullName || newUser.email} has been successfully created.`,
        variant: "success",
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error",
        description: response.message || response.error || "Failed to create user.",
        variant: "destructive",
      })
    },
  })
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) => userApi.updateUser(id, data),
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      queryClient.invalidateQueries({ queryKey: userKeys.detail(updatedUser.id) })

      toast({
        title: "User Updated",
        description: `${updatedUser.fullName || updatedUser.email} has been successfully updated.`,
        variant: "success",
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error",
        description: response.message || response.error || "Failed to update user.",
        variant: "destructive",
      })
    },
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => userApi.deleteUser(id).then(() => id),
    onSuccess: (deletedId) => {
      queryClient.removeQueries({ queryKey: userKeys.detail(deletedId) })
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })

      toast({
        title: "User Deleted",
        description: "User has been successfully deleted.",
        variant: "success",
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: "Error",
        description: response.message || response.error || "Failed to delete user.",
        variant: "destructive",
      })
    },
  })
}
